import { FormEvent, useMemo, useState } from 'react';
import {
  AlertTriangle, Building2, CheckCircle2, Download, KeyRound, Mail, ShieldCheck, Trash2, UserRound,
} from 'lucide-react';
import { fetchApi, refreshSession } from '../api';
import { useAuth } from '../context/AuthContext';
import '../styles/account.css';

const initials = (name = '') => name.split(/\s+/).filter(Boolean).map(part => part[0]).join('').slice(0, 2).toUpperCase() || 'CL';

/** Must match DELETE_CONFIRMATION in backend/src/services/account/AccountService.ts. */
const DELETE_CONFIRMATION = 'УДАЛИТЬ АККАУНТ';

export default function ProfilePage() {
  const { user, setUser, logout, refreshUser } = useAuth();
  const [name, setName] = useState(user?.name || '');
  const [company, setCompany] = useState(user?.company || '');
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  // Email confirmation
  const [resending, setResending] = useState(false);
  const [verifyNote, setVerifyNote] = useState('');

  // Danger zone
  const [exporting, setExporting] = useState(false);
  const [dangerNote, setDangerNote] = useState('');
  const [dangerError, setDangerError] = useState('');
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deletePassword, setDeletePassword] = useState('');
  const [deleteConfirmation, setDeleteConfirmation] = useState('');
  const [deleting, setDeleting] = useState(false);

  const isOwner = String(user?.role || 'OWNER').toUpperCase() === 'OWNER';
  const verified = Boolean(user?.emailVerified);

  const memberSince = useMemo(() => user?.createdAt
    ? `С нами с ${new Date(user.createdAt).toLocaleDateString('ru-RU', { month: 'long', year: 'numeric' })}`
    : 'Активный аккаунт', [user?.createdAt]);

  const saveProfile = async (event: FormEvent) => {
    event.preventDefault(); setSaving(true); setError(''); setMessage('');
    try {
      const updated = await fetchApi('/auth/me', { method: 'PATCH', body: JSON.stringify({ name: name.trim(), company: company.trim() }) });
      setUser((current: any) => ({ ...current, ...updated })); setMessage('Профиль обновлён');
    } catch (err: any) { setError(err.message || 'Не удалось сохранить профиль'); }
    finally { setSaving(false); }
  };

  const resendVerification = async () => {
    setResending(true); setVerifyNote('');
    try {
      const data = await fetchApi('/auth/resend-verification', { method: 'POST' });
      setVerifyNote(data?.message || 'Письмо отправлено. Проверьте входящие и папку «Спам».');
      if (data?.alreadyVerified) await refreshUser().catch(() => undefined);
    } catch (err: any) { setVerifyNote(err.message || 'Не удалось отправить письмо. Повторите позже.'); }
    finally { setResending(false); }
  };

  /**
   * Download the export as a file rather than through `fetchApi`, which would
   * parse the whole document into the JS heap first. A fresh access token comes
   * from `refreshSession`, the same call the app already makes on a timer.
   */
  const exportData = async () => {
    setExporting(true); setDangerError(''); setDangerNote('');
    try {
      const session = await refreshSession();
      const response = await fetch('/api/account/export', {
        headers: session?.token ? { Authorization: `Bearer ${session.token}` } : undefined,
        credentials: 'include',
      });
      if (!response.ok) {
        const payload = await response.json().catch(() => ({}));
        throw new Error(payload.error || 'Не удалось подготовить выгрузку. Повторите попытку.');
      }
      const disposition = response.headers.get('Content-Disposition') || '';
      const match = /filename="?([^";]+)"?/i.exec(disposition);
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = match?.[1] || `clientix-export-${new Date().toISOString().slice(0, 10)}.json`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
      setDangerNote('Файл выгрузки сохранён. Он содержит ваши персональные и рабочие данные; токены каналов в него не входят.');
    } catch (err: any) {
      setDangerError(err.message || 'Не удалось подготовить выгрузку');
    } finally { setExporting(false); }
  };

  const deleteAccount = async (event: FormEvent) => {
    event.preventDefault(); setDeleting(true); setDangerError(''); setDangerNote('');
    try {
      await fetchApi('/account', {
        method: 'DELETE',
        body: JSON.stringify({ password: deletePassword, confirmation: deleteConfirmation.trim() }),
      });
      await logout().catch(() => undefined);
      window.location.replace('/');
    } catch (err: any) {
      setDangerError(err.message || 'Не удалось удалить аккаунт');
      setDeleting(false);
    }
  };

  const confirmationReady = deleteConfirmation.trim() === DELETE_CONFIRMATION && deletePassword.length > 0;

  return <section className="view active account-page">
    <div className="account-heading"><div><span className="eyebrow">Аккаунт</span><h1>Профиль</h1><p>Личные данные, информация о компании и управление вашими данными.</p></div></div>
    <div className="profile-grid">
      <aside className="profile-summary-card">
        <div className="profile-avatar">{initials(user?.name)}</div><h2>{user?.name}</h2><p>{user?.email}</p><span className="profile-plan">{user?.plan || 'STARTER'}</span>
        <div className="profile-divider"/>
        <div className="profile-fact"><Building2 size={17}/><div><span>Компания</span><strong>{user?.company || 'Не указана'}</strong></div></div>
        <div className="profile-fact"><ShieldCheck size={17}/><div><span>Статус</span><strong>{memberSince}</strong></div></div>
        <div className="profile-fact"><Mail size={17}/><div><span>Email</span><strong>{verified ? 'Подтверждён' : 'Не подтверждён'}</strong></div></div>
      </aside>
      <div className="account-stack">
        <form className="account-card" onSubmit={saveProfile}>
          <header><span className="section-icon"><UserRound size={19}/></span><div><h2>Основная информация</h2><p>Эти данные видят ваши коллеги в Clientix.</p></div></header>
          {(message || error) && <div className={`form-message ${error ? 'error' : ''}`} role={error ? 'alert' : 'status'}><CheckCircle2 size={16}/>{error || message}</div>}
          <div className="form-grid">
            <label className="account-field"><span>Имя и фамилия</span><input required minLength={2} value={name} onChange={event => setName(event.target.value)} autoComplete="name"/></label>
            <label className="account-field"><span>Компания</span><input value={company} onChange={event => setCompany(event.target.value)} autoComplete="organization" placeholder="Название компании"/></label>
            <label className="account-field full"><span>Email</span><div className="locked-field"><Mail size={17}/><input value={user?.email || ''} readOnly aria-readonly="true"/></div><small>Для смены email обратитесь в поддержку.</small></label>
          </div>
          <footer><button className="btn btn-primary" disabled={saving || !name.trim()}>{saving ? 'Сохраняем…' : 'Сохранить изменения'}</button></footer>
        </form>

        <article className="account-card compact-card">
          <header><span className="section-icon neutral"><Mail size={19}/></span><div><h2>Подтверждение email</h2><p>Подтверждённый адрес нужен, чтобы подключать каналы и оплачивать тариф.</p></div></header>
          <div className="verify-row">
            <div className="verify-row-copy">
              <strong>{user?.email}</strong>
              <span>{verified
                ? `Адрес подтверждён${user?.emailVerifiedAt ? ` ${new Date(user.emailVerifiedAt).toLocaleDateString('ru-RU')}` : ''}. Все действия доступны.`
                : 'Мы отправили письмо со ссылкой при регистрации. Без подтверждения подключение каналов и оплата заблокированы — так аккаунт нельзя создать на чужую почту.'}</span>
            </div>
            {verified
              ? <span className="verify-chip ok"><CheckCircle2 size={13}/> Подтверждён</span>
              : <button type="button" className="btn btn-outline" onClick={resendVerification} disabled={resending}>
                  {resending ? 'Отправляем…' : 'Отправить письмо повторно'}
                </button>}
          </div>
          {verifyNote && <div className="form-message" role="status" style={{ marginTop: 12, marginBottom: 0 }}><CheckCircle2 size={16}/>{verifyNote}</div>}
        </article>

        <article className="account-card compact-card">
          <header><span className="section-icon neutral"><KeyRound size={19}/></span><div><h2>Безопасность аккаунта</h2><p>Управление паролем и активными сессиями.</p></div></header>
          <div className="security-row"><div><strong>Пароль</strong><span>Рекомендуем обновлять пароль каждые 90 дней</span></div><button type="button" className="btn btn-outline" onClick={() => window.location.hash = 'settings'}>Настроить безопасность</button></div>
        </article>

        {/* 152-ФЗ, ст. 14 и 21: доступ к своим данным и их удаление. */}
        <article className="account-card danger-card">
          <header><span className="section-icon danger-soft"><AlertTriangle size={19}/></span><div><h2>Ваши данные</h2><p>Выгрузка и безвозвратное удаление аккаунта — по 152-ФЗ это ваше право.</p></div></header>

          {(dangerNote || dangerError) && (
            <div className={`form-message ${dangerError ? 'error' : ''}`} role={dangerError ? 'alert' : 'status'}>
              <CheckCircle2 size={16}/>{dangerError || dangerNote}
            </div>
          )}

          <div className="danger-row">
            <div className="danger-row-copy">
              <strong>Выгрузка персональных данных</strong>
              <span>
                Один JSON-файл: профиль, команда, каналы, все диалоги с сообщениями, сделки, товары, платежи,
                уведомления, настройки ИИ и база знаний. Токены каналов и ключи интеграций в файл не попадают —
                это доступы, а не ваши данные.
              </span>
            </div>
            <button type="button" className="btn btn-outline" onClick={exportData} disabled={exporting || !isOwner}>
              <Download size={16}/>{exporting ? 'Готовим файл…' : 'Скачать мои данные'}
            </button>
          </div>

          <div className="danger-row">
            <div className="danger-row-copy">
              <strong>Удаление аккаунта</strong>
              <span>
                {isOwner
                  ? 'Удаляются профиль, рабочее пространство, участники, каналы, диалоги, сделки, товары и база знаний. Действие необратимо.'
                  : 'Удалить аккаунт может только владелец пространства. Попросите владельца убрать вас из команды — доступ закроется, а данные компании останутся у неё.'}
              </span>
            </div>
            {isOwner && !deleteOpen && (
              <button type="button" className="btn btn-danger-outline" onClick={() => { setDeleteOpen(true); setDangerError(''); setDangerNote(''); }}>
                <Trash2 size={16}/>Удалить аккаунт
              </button>
            )}
          </div>

          {isOwner && deleteOpen && (
            <form className="danger-confirm" onSubmit={deleteAccount}>
              <p><strong>Это необратимо.</strong> Восстановить данные после удаления невозможно — ни нами, ни из резервных копий.</p>
              <ul>
                <li>Будут удалены все диалоги, сообщения, сделки, товары и база знаний.</li>
                <li>Приглашённые сотрудники потеряют доступ к пространству.</li>
                <li>Бухгалтерские записи о совершённых платежах сохраняются в объёме, который требует закон.</li>
                <li>Сначала имеет смысл скачать выгрузку — после удаления это будет невозможно.</li>
              </ul>
              <label className="account-field">
                <span>Текущий пароль</span>
                <input type="password" value={deletePassword} onChange={event => setDeletePassword(event.target.value)} autoComplete="current-password" required/>
              </label>
              <label className="account-field">
                <span>Введите <span className="danger-code">{DELETE_CONFIRMATION}</span> для подтверждения</span>
                <input value={deleteConfirmation} onChange={event => setDeleteConfirmation(event.target.value)} placeholder={DELETE_CONFIRMATION} autoComplete="off" required/>
              </label>
              <div className="danger-confirm-actions">
                <button type="button" className="btn btn-outline" onClick={() => { setDeleteOpen(false); setDeletePassword(''); setDeleteConfirmation(''); }}>
                  Отмена
                </button>
                <button type="submit" className="btn btn-danger-outline" disabled={!confirmationReady || deleting}>
                  <Trash2 size={16}/>{deleting ? 'Удаляем…' : 'Удалить навсегда'}
                </button>
              </div>
            </form>
          )}
        </article>
      </div>
    </div>
  </section>;
}
